import { Injectable } from '@angular/core';
import { ApiResourceService } from "libraries/service/apiResource/apiResource.service";
import { Observable } from "rxjs";
import {
    IDetailPriceTraveller,
    IQuotationBlock,
    IQuotationCost,
    IQuotationExternalReference,
    IQuotationPriceTotal,
    IQuotationRetail,
    IQuotationSection,
    IQuotationsQuotation,
    IQuotationTraveller, IQuotationXrates,
} from "./IQuotationsQuotation";
import { CalculationMode } from "libraries/interfaces/IWenji";

const QUOTATION: string = 'quotation';
const QUOTATIONS: string = 'quotations';
const SECTIONS: string = 'sections';
const BLOCKS: string = 'blocks';

export interface IQuotationVat extends IQuotationCost {
    rate: number;
}

export interface IQuotationsQuotationCreate {
    title?: string;
    begin?: string;
    externalReference: IQuotationExternalReference;
    retail?: IQuotationRetail;
    cost?: IQuotationCost;
    sections: IQuotationQuotationsSectionsCreate[];
    travellers: IQuotationQuotationsTravellersCreate[];
    vat?: IQuotationVat;
    isReadOnly?: boolean;
    hasCommission?: boolean;
}

export interface IQuotationsQuotationSynchronize extends IQuotationsQuotationCreate {
    id: number;
}

export interface IQuotationQuotationsSectionsCreate {
    title: string;
    beginOffset?: number;
    duration?: number;
    externalReference?: IQuotationExternalReference;
    chapterCode: string;
    blocks: IQuotationQuotationsBlocksCreate[];
}

export interface IQuotationQuotationsBlocksCreate {
    active?: boolean;
    beginOffset?: number;
    category: string;
    comment?: string;
    cost?: IQuotationCost;
    duration?: number;
    externalReference?: IQuotationExternalReference;
    isChapo?: boolean;
    isOption: boolean;
    retail?: IQuotationRetail;
    //supplier?: number;
    supplier?: { code?: string, name?: string };
    title: string;
    totalPrices: IQuotationQuotationsPriceTotalCreate[];
    hasCommission?: boolean;
}

export interface IQuotationQuotationsBlockPatch {
    active?: boolean;
    beginOffset?: number;
    category?: string;
    comment?: string;
    cost?: IQuotationCost;
    duration?: number;
    order?: number;
    externalReference?: IQuotationExternalReference;
    isOption?: boolean;
    retail?: IQuotationRetail;
    retailIncludingVat?: IQuotationRetail;
    supplier?: { id?: number, name?: string };
    title?: string;
}

export interface IQuotationQuotationsTravellersCreate {
    firstName?: string;
    lastName?: string;
    travellerType: string;
    externalReference: IQuotationExternalReference;
    retail?: IQuotationRetail;
    cost?: IQuotationCost;
}

export interface IQuotationQuotationsPriceTotalCreate {
    cost: IQuotationCost;
    retail?: IQuotationRetail;
    externalReference?: IPriceTotalExternalReference;
    details?: IQuotationQuotationsPriceDetailCreate[];
}

export interface IQuotationQuotationsPriceDetailTravellerCreate {
    externalReference: IQuotationExternalReference;
    cost?: IQuotationCost;
    retail?: IQuotationCost;
}

export interface IPriceTotalExternalReference extends IQuotationExternalReference {
    type: EExternalReferenceType;
}

export enum EExternalReferenceType {
    Booking = 'booking',
    Quote = 'quote',
    Block = 'Block',
    Contract = 'contract'
}

export interface IQuotationQuotationsPriceDetailCreate {
    cost?: IQuotationCost;
    externalReference?: IQuotationExternalReference;
    title?: string;
    xType?: string;
    travellers?: IQuotationQuotationsPriceDetailTravellerCreate[];
}

export interface IQuotationsSuppliersSearch {
    id?: number;
    name: string;
    preferredCurrency?: string;
    currencies?: string[];
    categories?: string[];
    whenCreated?: string;
    whenLastUpdated?: string;
}

export interface IQuotationsSuppliersCreateInput {
    name: string;
    preferredCurrency?: string;
    currencies: string[];
    categories: string[];
}

export interface IQuotationQuotationCategoriesMarginPut {
    categoryRates: ICategoryRates;
    optionBlocksOnly: boolean;
}

export interface ICategoryRates {
    [key: string]: number;
}

@Injectable()
export class QuotationQuotationsResourceService {

    constructor(private readonly apiResourceService: ApiResourceService) {
    }

    public create(request: IQuotationsQuotationCreate): Observable<IQuotationsQuotation> {
        return this.apiResourceService.postOne<IQuotationsQuotationCreate, IQuotationsQuotation>(
            this.apiResourceService.getApiUrl(QUOTATION, QUOTATIONS, 'create'),
            request
        );
    }

    public synchronize(requestData: IQuotationsQuotationSynchronize): Observable<IQuotationsQuotation> {
        const {id, ...request}: IQuotationsQuotationSynchronize = requestData;
        return this.apiResourceService.postOne<IQuotationsQuotationCreate, IQuotationsQuotation>(
            this.apiResourceService.getApiUrl(QUOTATION, QUOTATIONS, id, 'synchronize'),
            request
        );
    }

    public get(quotationId: number): Observable<IQuotationsQuotation> {
        return this.apiResourceService.getOne<IQuotationsQuotation>(
            this.apiResourceService.getApiUrl(QUOTATION, QUOTATIONS, `${quotationId}`)
        );
    }

    public duplicate(quotationId: number): Observable<IQuotationsQuotation> {
        return this.apiResourceService.postOne<void, IQuotationsQuotation>(
            this.apiResourceService.getApiUrl(QUOTATION, QUOTATIONS, `${quotationId}`, 'duplicate'),
            undefined
        );
    }

    public patch(request: Partial<IQuotationsQuotationCreate>, quotationId: number): Observable<IQuotationsQuotation> {
        return this.apiResourceService.patchOne<Partial<IQuotationsQuotationCreate>, IQuotationsQuotation>(
            this.apiResourceService.getApiUrl(QUOTATION, QUOTATIONS, quotationId),
            request
        );
    }

    public createSection(request: IQuotationQuotationsSectionsCreate, quotationId: number): Observable<IQuotationSection> {
        return this.apiResourceService.postOne(
            this.apiResourceService.getApiUrl(QUOTATION, QUOTATIONS, `${quotationId}`, SECTIONS, 'create'),
            request,
        );
    }

    public createBlock(request: IQuotationQuotationsBlocksCreate, sectionId: string): Observable<IQuotationBlock> {
        return this.apiResourceService.postOne(
            this.apiResourceService.getApiUrl(QUOTATION, SECTIONS, sectionId, BLOCKS, 'create'),
            request,
        );
    }

    public patchBlock(request: IQuotationQuotationsBlockPatch, blockId: number): Observable<IQuotationsQuotation> {
        return this.apiResourceService.patchOne<IQuotationQuotationsBlockPatch, IQuotationsQuotation>(
            this.apiResourceService.getApiUrl(QUOTATION, BLOCKS, blockId),
            request,
            {'return': QUOTATION}
        );
    }

    public deleteBlock(blockId: number): Observable<void> {
        return this.apiResourceService.deleteOne<null>(
            this.apiResourceService.getApiUrl(QUOTATION, BLOCKS, blockId),
        );
    }

    public patchDetailPriceTravellerCostReturn(id: number, cost: IQuotationCost): Observable<IQuotationsQuotation> {
        return this.apiResourceService.patchOne(
            this.apiResourceService.getApiUrl(QUOTATION, 'travellerPriceDetails', id),
            {
                cost: {
                    value: cost.value,
                }
            },
            {'return': QUOTATION}
        );
    }

    public patchTotalPriceCurrency(id: number, cost: IQuotationCost): Observable<IQuotationsQuotation> {
        return this.apiResourceService.patchOne(
            this.apiResourceService.getApiUrl(QUOTATION, 'pricestotal', id),
            {
                cost: {
                    currency: cost.currency,
                }
            },
            {'return': QUOTATION}
        );
    }

    public patchTotalPrice(body: IQuotationPriceTotal, id: number): Observable<IQuotationsQuotation> {
        return this.apiResourceService.patchOne(
            this.apiResourceService.getApiUrl(QUOTATION, 'pricestotal', id),
            body,
            {'return': QUOTATION}
        );
    }

    public patchQuotationBlock(id: number, body: unknown): Observable<IQuotationsQuotation> {
        return this.apiResourceService.patchOne(
            this.apiResourceService.getApiUrl(QUOTATION, BLOCKS, id),
            body,
            {'return': QUOTATION}
        );
    }

    public patchBlockDetailPrice(id: number, cost: IQuotationCost): Observable<IQuotationsQuotation> {
        return this.apiResourceService.patchOne(
            this.apiResourceService.getApiUrl(QUOTATION, 'pricedetails', id),
            {
                cost: {
                    value: cost.value,
                }
            },
            {'return': QUOTATION}
        );
    }

    public patchDetailPriceTravellerCostSimple(id: number, cost: IQuotationCost): Observable<IDetailPriceTraveller> {
        return this.apiResourceService.patchOne(
            this.apiResourceService.getApiUrl(QUOTATION, 'travellerPriceDetails', id),
            {
                cost: {
                    value: cost.value,
                }
            },
        );
    }

    public getQuotationSuppliers(): Observable<IQuotationsSuppliersSearch[]> {
        return this.apiResourceService.get(
            this.apiResourceService.getApiUrl(QUOTATION, 'suppliers')
        );
    }

    public searchQuotationSuppliers(body: { name: string, category: string }): Observable<IQuotationsSuppliersSearch[]> {
        return this.apiResourceService.post(
            this.apiResourceService.getApiUrl(QUOTATION, 'suppliers/search'),
            body
        );
    }

    public createSupplier(body: IQuotationsSuppliersCreateInput): Observable<IQuotationsSuppliersSearch> {
        return this.apiResourceService.postOne(
            this.apiResourceService.getApiUrl(QUOTATION, 'suppliers', 'create'),
            body
        );
    }

    public patchTraveller(body: IQuotationTraveller, travellerId: number): Observable<IQuotationsQuotation> {
        return this.apiResourceService.patchOne(
            this.apiResourceService.getApiUrl(QUOTATION, 'travellers', travellerId),
            body,
            {'return': QUOTATION}
        );
    }

    public putQuotationQuotationsCategories(quotationId: number, calculationMode: CalculationMode, request: IQuotationQuotationCategoriesMarginPut)
        : Observable<IQuotationsQuotation> {
        return this.apiResourceService.putOne(
            this.apiResourceService.getApiUrl(QUOTATION, QUOTATIONS, quotationId, "categories", calculationMode),
            request
        );
    }

    public propagate(quotationId: number): Observable<unknown> {
        return this.apiResourceService.postOne(
            this.apiResourceService.getApiUrl(QUOTATION, QUOTATIONS, quotationId, "propagate"),
            null
        );
    }

    public refreshXRates(quotationId: number): Observable<IQuotationsQuotation> {
        return this.apiResourceService.postOne(
            this.apiResourceService.getApiUrl("quotation", "quotations", quotationId, "xrates", "refresh"),
            null
        );
    }

    public getExchangeRates(body: object): Observable<IQuotationXrates[]> {
        return this.apiResourceService.post(
            this.apiResourceService.getApiUrl("price", "xrates", 'search'),
            body
        );
    }

    public getBlock(blockId: string): Observable<object> {
        return this.apiResourceService.getOne(
            this.apiResourceService.getApiServerUrl("watabi", "item", blockId)
        );
    }

    public generateRequest(block: object, quoteBlock: IQuotationBlock): object {
        const price: object = {
            cost: {total: quoteBlock.cost},
            retail: {
                total: {
                    amount: quoteBlock.retail.amount,
                    currency: quoteBlock.retail.currency,
                    value: quoteBlock.retail.value,
                    decimals: quoteBlock.retail.decimals,
                }
            }
        };
        return prepareItemForUpdate({...block, price});
    }

    public updateBlock(request: object): Observable<object> {
        return this.apiResourceService.post(
            this.apiResourceService.getApiServerUrl("watabi", "item"),
            request
        );
    }
}

function prepareItemForUpdate(params: object): object {

    const {
        descriptions, medias, ui, days,
        quotes, sequences, blocks, sections,
        flags, idVersionOrigin, idOrigin, destinations,
        whoLastUpdated, whenLastUpdated, shared,
        idRoot, idVersionRoot, links, index, ...filteredBody
    }: any = params;

    return {...filteredBody, action: 'U'};
}

